import React, { useState, useEffect, useRef } from "react";
import { Text } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import CostSummaryCards from "@src/features/Dashboard/CostSummaryCards/CostSummaryCards";
import CostChart from "@src/features/Dashboard/CostChart/CostChart";
import EventCountChart from "@src/features/Dashboard/EventCountChart/EventCountChart";
import UserCountChart from "@src/features/Dashboard/UserCountChart/UserCountChart";
import CostBreakdownTable from "@src/features/Dashboard/CostBreakdownTable/CostBreakdownTable";
import DateRangeFilter from "@src/components/DateRangeFilter/DateRangeFilter";
import ProcessTimeSeriesChart from "@src/features/Dashboard/ProcessTimeSeriesChart/ProcessTimeSeriesChart";
import type { CostFilterParams, CostDimension } from "@models/cost";
import "./DashboardPage.scss";
import { useCost } from "@src/hooks/useCost";
import { useMonitoring } from "@src/hooks/useMonitoring";
import dayjs from "dayjs";
import { useAdminPermissions } from "@src/hooks/useAdminPermissions";

const DashboardPage: React.FC = () => {
    const { isSuperAdmin, canViewDashboard } = useAdminPermissions();
    const [filters, setFilters] = useState<CostFilterParams>(() => ({
        startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
    }));
    const [selectedDimension, setSelectedDimension] =
        useState<CostDimension>("module");

    const { summary, timeSeries, breakdown, isSummaryLoading, isTimeSeriesLoading, isBreakdownLoading, error, fetchAll, fetchBreakdown } =
        useCost();
    const {
        summary: processSummary,
        timeSeries: processTimeSeries,
        isLoading: isMonitoringLoading,
        error: monitoringError,
        fetchDashboardData,
    } = useMonitoring();

    const callbacksRef = useRef({ fetchAll, fetchDashboardData, fetchBreakdown });
    callbacksRef.current = { fetchAll, fetchDashboardData, fetchBreakdown };

    useEffect(() => {
        callbacksRef.current.fetchAll(selectedDimension, "day", filters);

        if (!filters.startDate || !filters.endDate) {
            return;
        }

        callbacksRef.current.fetchDashboardData(filters.startDate, filters.endDate);
    }, [filters, selectedDimension]);

    const handleFilterChange = (newFilters: CostFilterParams) => {
        setFilters(newFilters);
    };

    const handleDimensionChange = (dimension: CostDimension) => {
        setSelectedDimension(dimension);
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <Text size="lg" fw={600} component="h1" className="dashboard-title">
                    Cost Dashboard
                </Text>
                <DateRangeFilter
                    onFilterChange={handleFilterChange}
                    initialFilters={filters}
                />
            </div>

            {error && <Alert color="red">{error}</Alert>}
            {monitoringError && <Alert color="red">{monitoringError}</Alert>}

            <CostSummaryCards
                data={summary}
                processData={processSummary}
                isLoading={isSummaryLoading || isMonitoringLoading}
                showProcessCards
            />

            <div className="dashboard-charts-grid">
                <CostChart data={timeSeries} isLoading={isTimeSeriesLoading} />
                <EventCountChart data={timeSeries} isLoading={isTimeSeriesLoading} />
            </div>

            <div className="dashboard-charts-half">
                <UserCountChart data={timeSeries} isLoading={isTimeSeriesLoading} />
                <ProcessTimeSeriesChart
                    data={processTimeSeries}
                    isLoading={isMonitoringLoading}
                />
            </div>

            {isSuperAdmin && canViewDashboard && <CostBreakdownTable
                data={breakdown}
                isLoading={isBreakdownLoading}
                selectedDimension={selectedDimension}
                onDimensionChange={handleDimensionChange}
            />}
        </div>
    );
};

export default DashboardPage;
